import { useCallback, useMemo } from "react";
import { Alert, Button, Group, Radio, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconAlertTriangle,
  IconDeviceFloppy,
  IconInfoCircle,
} from "@tabler/icons-react";
import type { SlidePrintRoutingMode } from "../../../services/slidePrintRouting.service";

interface FormValues {
  mode: SlidePrintRoutingMode;
}

interface SlidePrintRoutingSettingsFormProps {
  currentMode: SlidePrintRoutingMode;
  updatedAt: string | null;
  saving: boolean;
  saveError?: string;
  onSave: (mode: SlidePrintRoutingMode) => void;
}

function validateRoutingMode(value: SlidePrintRoutingMode): string | null {
  return value === "backend_only" || value === "local_with_backend_fallback"
    ? null
    : "Select a supported slide printing route.";
}

export function SlidePrintRoutingSettingsForm({
  currentMode,
  updatedAt,
  saving,
  saveError,
  onSave,
}: SlidePrintRoutingSettingsFormProps) {
  const form = useForm<FormValues>({
    initialValues: { mode: currentMode },
    validateInputOnBlur: true,
    validate: { mode: validateRoutingMode },
  });
  const selectedLegacyMode = useMemo(
    () => form.values.mode === "local_with_backend_fallback",
    [form.values.mode]
  );
  const lastUpdated = useMemo(
    () => (updatedAt ? new Date(updatedAt).toLocaleString() : "Not yet saved"),
    [updatedAt]
  );
  const handleModeChange = useCallback(
    (value: string) => {
      form.setFieldValue("mode", value as SlidePrintRoutingMode);
    },
    [form]
  );
  const handleSubmit = useCallback(
    (values: FormValues) => {
      onSave(values.mode);
    },
    [onSave]
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="lg">
        <Alert
          icon={<IconInfoCircle size={18} />}
          color="blue"
          variant="light"
          title="The setting is checked before every print"
        >
          Open cutting screens use the latest saved route without needing a
          refresh or redeployment.
        </Alert>

        <Radio.Group
          label="Slide-label printing route"
          description="Choose how new slide-label batches reach the configured printer."
          value={form.values.mode}
          error={form.errors.mode}
          onChange={handleModeChange}
        >
          <Stack gap="sm" mt="sm">
            <Radio.Card value="backend_only" withBorder p="md">
              <Group wrap="nowrap" align="flex-start">
                <Radio.Indicator />
                <div>
                  <Text fw={600}>Backend relay only — Recommended</Text>
                  <Text size="sm" c="dimmed">
                    Send one durable backend bridge command and do not attempt a
                    direct local print.
                  </Text>
                </div>
              </Group>
            </Radio.Card>
            <Radio.Card value="local_with_backend_fallback" withBorder p="md">
              <Group wrap="nowrap" align="flex-start">
                <Radio.Indicator />
                <div>
                  <Text fw={600}>
                    Local bridge with backend fallback — Legacy
                  </Text>
                  <Text size="sm" c="dimmed">
                    Try the local bridge first and resend through the backend if
                    confirmation is lost.
                  </Text>
                </div>
              </Group>
            </Radio.Card>
          </Stack>
        </Radio.Group>

        {selectedLegacyMode && (
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="yellow"
            variant="light"
            title="Duplicate-label risk"
            role="alert"
          >
            If the local printer accepts a batch but its response is lost, the
            backend fallback can print the same labels again.
          </Alert>
        )}

        {saveError && (
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="red"
            variant="light"
            title="Setting not saved"
            role="alert"
          >
            {saveError}
          </Alert>
        )}

        <Text size="sm" c="dimmed">
          Last updated {lastUpdated}
        </Text>

        <Button
          type="submit"
          loading={saving}
          leftSection={<IconDeviceFloppy size={16} />}
          style={{ alignSelf: "flex-start" }}
        >
          Save slide printing route
        </Button>
      </Stack>
    </form>
  );
}
